import { useSiteStore } from '@/stores/site';
import { useProductStore } from '@/stores/product';
import LayerType, { LayerCategory } from '@/types/LayerType';

/**
 * Style d'icones unifie : Phosphor Icons (ph:*-duotone).
 * Les duotone donnent une teinte accent + trait, plus moderne que le mix
 * line/fill de Remix Icon utilise historiquement. En cas de fallback,
 * `ph:*` (regular) fonctionne aussi.
 */
export const layerTypes: LayerType[] = [
    {
        id: 'image',
        featureName: 'Image',
        label: 'Image',
        icon: 'ph:image-duotone',
        category: 'images',
        // @ts-ignore
        form: defineAsyncComponent(() => import('@/components/Layer/Form/LayerFormImage.vue')),
        // @ts-ignore
        view: defineAsyncComponent(() => import('@/components/Layer/View/LayerViewImage.vue')),
        // @ts-ignore
        rowView: defineAsyncComponent(() => import('@/components/Layer/RowView/LayerRowViewImage.vue'))
    },
    {
        id: 'photoFrame',
        featureName: 'Image',
        label: 'PhotoFrame',
        icon: 'ph:frame-corners-duotone',
        category: 'images',
        // @ts-ignore
        form: defineAsyncComponent(() => import('@/components/Layer/Form/LayerFormPhotoFrame.vue'))
    },
    {
        id: 'aiImage',
        featureName: 'AiImage',
        label: 'AiImage',
        icon: 'ph:sparkle-duotone',
        category: 'images',
        // @ts-ignore
        form: defineAsyncComponent(() => import('@/components/Layer/Form/LayerFormAiImage.vue'))
    },
    {
        id: 'clipart',
        featureName: 'Cliparts',
        label: 'Cliparts',
        icon: 'ph:smiley-sticker-duotone',
        category: 'images',
        // @ts-ignore
        form: defineAsyncComponent(() => import('@/components/Layer/Form/LayerFormClipart.vue'))
    },
    {
        id: 'shape',
        featureName: 'Shape',
        label: 'Shape',
        icon: 'ph:shapes-duotone',
        category: 'decor',
        // @ts-ignore
        form: defineAsyncComponent(() => import('@/components/Layer/Form/LayerFormShape.vue'))
    },
    {
        id: 'text',
        featureName: 'Texts',
        label: 'Text',
        icon: 'ph:text-aa-duotone',
        category: 'text',
        // @ts-ignore
        form: defineAsyncComponent(() => import('@/components/Layer/Form/LayerFormText.vue'))
    },
    {
        // VDP / publipostage. Gating spécifique (produit.vdpEnabled + un seul champ
        // par création) géré dans getAvailableLayerTypes, PAS via featureName.
        id: 'variableText',
        label: 'VariableText',
        icon: 'ph:list-numbers-duotone',
        category: 'text',
        // @ts-ignore
        form: defineAsyncComponent(() => import('@/components/Layer/Form/LayerFormVariableText.vue'))
    },
    {
        id: 'qrcode',
        featureName: 'QrCode',
        label: 'QrCode',
        icon: 'ph:qr-code-duotone',
        category: 'text',
        // @ts-ignore
        form: defineAsyncComponent(() => import('@/components/Layer/Form/LayerFormQrCode.vue'))
    },
    {
        id: 'group',
        featureName: 'Groups',
        label: '',
        icon: '',
        // @ts-ignore
        form: defineAsyncComponent(() => import('@/components/Layer/Form/LayerFormColors.vue')),
        // @ts-ignore
        rowView: defineAsyncComponent(() => import('@/components/Layer/LayerRowColors.vue'))
    },
    {
        id: 'backpattern',
        featureName: 'Patterns',
        label: 'Pattern',
        icon: 'ph:flower-duotone',
        category: 'decor',
        route: '//products/[productId]/customize.pattern',
        // @ts-ignore
        view: defineAsyncComponent(() => import('@/components/Layer/Form/LayerFormColors.vue'))
    },
    {
        id: 'theme',
        featureName: 'Themes',
        label: 'Theme',
        icon: 'ph:paint-brush-broad-duotone',
        category: 'templates',
        route: '//products/[productId]/customize.theme'
    },
    {
        id: 'graduation',
        featureName: 'Measures',
        label: 'Graduation',
        icon: 'ph:ruler-duotone',
        category: 'tools',
        route: '//products/[productId]/customize.graduation'
    }
];

/**
 * Ordre d'affichage des sections dans la feuille "Ajouter un element" + cle i18n
 * du titre. Du plus structurant (partir d'un modele) au plus ponctuel (outils).
 */
export const LAYER_CATEGORY_ORDER: { id: LayerCategory; labelKey: string }[] = [
    { id: 'text', labelKey: 'LayerSectionText' },
    { id: 'images', labelKey: 'LayerSectionImages' },
    { id: 'decor', labelKey: 'LayerSectionDecor' },
    { id: 'tools', labelKey: 'LayerSectionTools' },
    { id: 'templates', labelKey: 'LayerSectionTemplates' }
];

export interface LayerTypeGroup {
    id: LayerCategory;
    labelKey: string;
    items: LayerType[];
}

/**
 * Types de calques disponibles, regroupes par categorie dans l'ordre
 * LAYER_CATEGORY_ORDER. Une section sans item (apres gating) est omise. Les
 * types sans libelle (ex: 'group' interne) sont exclus des cartes.
 */
export function getAvailableLayerGroups(): LayerTypeGroup[] {
    const available = getAvailableLayerTypes().filter((lt) => lt.label && lt.icon);
    return LAYER_CATEGORY_ORDER.map(({ id, labelKey }) => ({
        id,
        labelKey,
        items: available.filter((lt) => lt.category === id)
    })).filter((group) => group.items.length > 0);
}

export function getAvailableLayerTypes(): LayerType[] {
    const siteStore = useSiteStore();
    const productStore = useProductStore();
    return layerTypes
        .map((layerType) => {
            // VDP (texte variable) : 3 états au niveau du site.
            //  - 'off'      => jamais proposé.
            //  - 'always'   => proposé sur tous les produits.
            //  - 'onDemand' => proposé uniquement si la VDP est demandée pour ce
            //                  produit (cf. productStore.isVdpRequested : réglage
            //                  du produit C3D au BO, OU demande de l'hôte
            //                  e-commerce via `sendEcommerceProduct.vdpEnabled` /
            //                  `?vdp=1`) — facturation dédiée.
            if (layerType.id === 'variableText') {
                const mode = siteStore.vdpMode();
                if (mode === 'off') {
                    return null;
                }
                if (mode === 'onDemand' && !productStore.isVdpRequested) {
                    return null;
                }
                // Gating PAR MÉTHODE : le profil d'impression actif peut interdire la
                // VDP (BO). En sérigraphie/broderie (plaques/fils fixes), les données
                // variables par exemplaire n'ont pas de sens → le BO décoche vdpEnabled.
                // ABSENT/true => autorisé (forward-compat) ; seul un `false` bloque.
                const profile = productStore.selectedPrintProfile as { vdpEnabled?: boolean } | null;
                if (profile?.vdpEnabled === false) {
                    return null;
                }
                return layerType;
            }
            // Graduation : proposée uniquement si le produit a des graduations
            // LIÉES. Aucune graduation => on ne propose pas l'outil en FO.
            if (layerType.id === 'graduation' && (productStore.graduations?.length ?? 0) === 0) {
                return null;
            }
            // Image IA : produit du RASTER (ne suit pas les encres) → NON proposée
            // en méthode palette (sérigraphie / broderie).
            if (layerType.id === 'aiImage') {
                const method = productStore.selectedPrintProfile?.method ?? 'digital';
                if (method === 'screen' || method === 'embroidery') {
                    return null;
                }
            }
            // Cadre photo : produit une image PHOTO/raster → n'a de sens qu'en
            // NUMÉRIQUE (sérigraphie/broderie/gravure = palette/monochrome). Désactivé
            // dès que la méthode d'impression n'est pas digitale.
            if (layerType.id === 'photoFrame') {
                const method = productStore.selectedPrintProfile?.method ?? 'digital';
                if (method !== 'digital') {
                    return null;
                }
            }
            // Gating par feature : forward-compat => une feature ABSENTE du site
            // (nouvellement introduite, site pas encore re-sauvegardé en BO) reste
            // ACTIVÉE ; on ne masque que si l'admin l'a explicitement décochée.
            if (layerType.featureName && !siteStore.isFeatureAvailable(layerType.featureName)) {
                return null;
            }
            return layerType;
        })
        .filter((lt): lt is LayerType => lt !== null);
}
